import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import noop from 'lodash/noop';
import { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { integrationsApi } from 'apis/integrations';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import styles from './emailDomainModal.module.scss';

export type FieldValues = {
  domain: string;
};

/** A component that displays add email domain modal. */
export const EmailDomainModal = createAppModal(
  { type: 'settings:addEmailDomain' },
  ({ isOpen, hide }): JSX.Element => {
    const notify = useNotifications();

    const { formState, control, handleSubmit, resetField } =
      useForm<FieldValues>({
        defaultValues: {
          domain: '',
        },
        mode: 'all',
      });

    const handleAddEmailDomain = useCallback(
      async (fieldValues: FieldValues) => {
        try {
          const createResponse = await createEmailDomain({
            domain: fieldValues.domain,
          }).unwrap();

          if (isResponseWithData(createResponse)) {
            notify(
              {
                title: 'Success',
                text: 'Email domain added successfully',
                icon: 'i-done',
                time: 5000,
              },
              NotificationType.Success,
            );

            handleClose();
          }
        } catch (e) {
          notify(
            {
              title: 'An error occurred while adding email domain',
            },
            NotificationType.Error,
          );
        }
      },
      [],
    );

    /** Handles closing the modal. */
    const handleClose = () => {
      resetField('domain');
      hide?.();
    };

    const [createEmailDomain] = integrationsApi.useCreateEmailDomainMutation();

    const handleValidSubmit: SubmitHandler<FieldValues> = async (
      fieldValues,
    ) => {
      await handleAddEmailDomain(fieldValues);
    };

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <form
          noValidate
          tabIndex={-1}
          onSubmit={handleSubmit(handleValidSubmit)}
        >
          <ModalHead title="Add email domain" onClose={handleClose} />

          <ModalBody>
            <FormGrid>
              <FormField>
                <HookFormText
                  name="domain"
                  label="Email domain"
                  control={control}
                  componentProps={{
                    placeholder: 'Add email domain',
                  }}
                  rules={{
                    required: 'Email domain is required',
                  }}
                ></HookFormText>
              </FormField>
            </FormGrid>
          </ModalBody>

          <ModalFoot
            cancelOnClick={handleClose}
            confirmName="Add"
            confirmOnClick={noop} // A hack to show confirm button
            confirmProps={{
              disabled: !(formState.isValid && formState.isDirty),
              type: 'submit',
              isLoading: formState.isSubmitting,
            }}
          />
        </form>
      </Modal>
    );
  },
);
